import type { CalendarField, CalendarSlot } from '@/services/bookingCalendarService';
import { getFieldSlotStepMinutes, hourSpan, selectionKey } from '@/components/admin/booking/bookingCalendarUtils';

export interface DayBlock {
  start: string;
  end: string;
  span: number;
  slots: CalendarSlot[];
  kind: 'open' | 'booked' | 'blocked' | 'locked' | 'selected';
}

export function buildDayBlocks(
  slots: CalendarSlot[],
  date: string,
  selected: Set<string>,
  stepMinutes?: number,
): Map<string, DayBlock> {
  const daySlots = slots.filter((slot) => slot.date === date).sort((a, b) => a.start.localeCompare(b.start));
  const blocks = new Map<string, DayBlock>();
  const covered = new Set<string>();
  const effectiveStep = stepMinutes && stepMinutes > 0 ? stepMinutes : getFieldSlotStepMinutes(daySlots.length > 0 ? daySlots : slots);

  daySlots.forEach((slot, index) => {
    if (covered.has(slot.public_id)) return;
    if (slot.state === 'booked' && slot.order_number) {
      const group = [slot];
      let end = slot.end;
      for (let nextIndex = index + 1; nextIndex < daySlots.length; nextIndex += 1) {
        const next = daySlots[nextIndex];
        if (next.state === 'booked' && next.order_number === slot.order_number && next.start === end) {
          group.push(next);
          end = next.end;
          covered.add(next.public_id);
        }
      }
      blocks.set(slot.start, {
        start: slot.start,
        end,
        span: hourSpan(slot.start, end, effectiveStep),
        slots: group,
        kind: 'booked',
      });
      return;
    }
    const selectedSlot = selected.has(selectionKey(slot.date, slot.public_id));
    blocks.set(slot.start, {
      start: slot.start,
      end: slot.end,
      span: 1,
      slots: [slot],
      kind: selectedSlot ? 'selected' : slot.state,
    });
  });

  return blocks;
}

export interface MonthEvent {
  key: string;
  time: string;
  name: string;
  status: string;
  slot: CalendarSlot;
  field: CalendarField;
}

export function monthEvents(fields: CalendarField[]): Map<string, MonthEvent[]> {
  const grouped = new Map<string, MonthEvent[]>();
  fields.forEach((field) => {
    const seen = new Set<string>();
    field.slots.filter((slot) => slot.state === 'booked').forEach((slot) => {
      const identity = `${slot.date}|${slot.order_number || slot.public_id}`;
      if (seen.has(identity)) return;
      seen.add(identity);
      const group = field.slots.filter((item) => item.date === slot.date && item.order_number === slot.order_number && item.state === 'booked');
      const end = group.reduce((latest, item) => (item.end > latest ? item.end : latest), slot.end);
      const list = grouped.get(slot.date) ?? [];
      list.push({
        key: identity,
        time: slot.start,
        name: slot.customer_name || 'Booking',
        status: slot.payment_status || 'unpaid',
        slot: { ...slot, end },
        field,
      });
      grouped.set(slot.date, list);
    });
  });
  grouped.forEach((events) => events.sort((a, b) => a.time.localeCompare(b.time)));
  return grouped;
}
